import { Alert, Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { fmtDashboardName } from "../../utils/format.util";
import { useAuthUser } from "../../hooks/useAuthUser";
import { RpiNotification, useRpiNotifications } from "../../hooks/useRpiNotifications";
import React from "react";
import { IconV3 } from "@rpi/openapi-core";
import { CreatePaymentMethod } from "../admin/billing/PaymentMethodCreateViewV2";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { Body, Heading } from "../../components/Typography";
import { YtdOrdersChart } from "./charts/YtdOrdersChart";
import { YtdPaymentsChart } from "./charts/YtdPaymentsChart";

const useStyles = createStyles(() => ({
  container: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    backgroundColor: tokens.color.primitive.light.calm[50],
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[10],
  },
  header: {
    fontFamily: tokens.font.family.heading,
    "font-weight": tokens.font.weight.semibold,
    fontSize: tokens.font.size["3xl"],
    lineHeight: tokens.font.lineHeight.tight,
    color: tokens.color.semantic.text.bold,
  },
  trends: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[6],
  },
  trendsHeader: {
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.lg,
    "font-weight": tokens.font.weight.bold,
    lineHeight: tokens.font.lineHeight.relaxed,
    color: tokens.color.semantic.text.subtle,
  },
  charts: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacing[10],
  },
  chartCard: {
    flex: 1,
    padding: tokens.spacing[6],
    width: "100%",
    minWidth: 528,
    height: 494,
    backgroundColor: tokens.color.primitive.base.white,
    borderRadius: tokens.radius.sm,
    boxShadow: "0 4px 12px 1px rgba(0, 0, 0, 0.10)",
  },
}));

const useAlertStyles = createStyles({
  paymentAlert: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacing[1],
  },
  alertContent: {
    display: "flex",
    justifyContent: "flex-start",
    gap: tokens.spacing[2],
  },
  alertText: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[1],
  },
  alertTitle: {
    color: tokens.color.semantic.text.bold,
  },
  alertDescription: {
    color: tokens.color.semantic.text.subtle,
  },
  addPaymentDrawer: {
    padding: tokens.spacing[6],
    paddingTop: 0,
  },
  addPaymentButton: {
    flexShrink: 0,
    flexGrow: 0,
    alignSelf: "center",
  },
});

const OrdersChartCard = () => {
  const { classes } = useStyles();

  return (
    <div className={classes.chartCard}>
      <YtdOrdersChart />
    </div>
  );
};

const PaymentsChartCard = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.chartCard}>
      <YtdPaymentsChart />
    </div>
  );
};

const AddPaymentMethod = () => {
  const user = useAuthUser();
  const { classes } = useAlertStyles();
  const { notifications } = useRpiNotifications();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const showPaymentNotification = React.useMemo(
    () => user.is.CUSTOMER_ADMIN && notifications[RpiNotification.PAYMENT_METHOD],
    [notifications, user]
  );

  const addPaymentMethod = React.useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = React.useCallback(() => setDrawerOpen(false), []);

  if (!user.is.CUSTOMER_ADMIN || user.isSandbox || !showPaymentNotification) return null;

  return (
    <>
      <Alert type="error" size="large">
        <div className={classes.paymentAlert}>
          <div className={classes.alertContent}>
            <IconV3 size={24} color={tokens.color.semantic.icon.danger} icon="error" />
            <div className={classes.alertText}>
              <Heading className={classes.alertTitle}>Add your payment method</Heading>
              <Body size="sm" className={classes.alertDescription}>
                You need to provide a payment method to set up the API integration. Orders will not process correctly
                until this is added.
              </Body>
            </div>
          </div>
          <Button className={classes.addPaymentButton} size="small" onClick={addPaymentMethod}>
            Add Payment Method
          </Button>
        </div>
      </Alert>
      <RpaDrawer title="Add payment method" opened={drawerOpen} onClose={closeDrawer}>
        <div className={classes.addPaymentDrawer}>
          <CreatePaymentMethod onComplete={closeDrawer} onCancel={closeDrawer} />
        </div>
      </RpaDrawer>
    </>
  );
};

export const DashboardView = () => {
  const user = useAuthUser();
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <span className={classes.header}>{`Welcome back, ${fmtDashboardName(user.dashboardUser)}!`}</span>
      <AddPaymentMethod />
      <div className={classes.trends}>
        <span className={classes.trendsHeader}>TRENDS</span>
        <div className={classes.charts}>
          <OrdersChartCard />
          <PaymentsChartCard />
        </div>
      </div>
    </div>
  );
};
